import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";
import SelectComponent from "@via/components/select/select";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useCreateIntegrationToken } from "@via/api/integration-tokens.api";
import { useIntegrationTokens } from "@via/api/integration-tokens.api";
import {
  getTokenTypeIcon,
  tokenTypeOptions,
} from "@via/pages/account/profile/integration-tokens/utils/helper";
import { requiredValidation } from "@via/utils/form-validators";
import {
  aiServiceTokenTypes,
  CreateIntegrationTokenPayload,
  modelSmithServiceTokenTypes,
  ServiceToken,
  ServiceTokenType,
} from "common";
import { type JSX, useEffect } from "react";
import { useForm } from "react-hook-form";
import CreateGithubToken from "@via/pages/account/profile/integration-tokens/components/integration-token-types/create-github-token.tsx";
import CreateGitLabToken from "@via/pages/account/profile/integration-tokens/components/integration-token-types/create-gitlab-token.tsx";
import CreateBitbucketToken from "@via/pages/account/profile/integration-tokens/components/integration-token-types/create-bitbucket-token.tsx";
import CreateDefaultToken from "@via/pages/account/profile/integration-tokens/components/integration-token-types/create-default-token.tsx";

const CreateFormComponentMap = {
  github: CreateGithubToken,
  gitlab: CreateGitLabToken,
  bitbucket: CreateBitbucketToken,
};

export default function CreateIntegrationTokenForm({
  setOpen,
  givenTokenType,
  onSuccess,
}: {
  setOpen: (open: boolean) => void;
  givenTokenType?: string;
  onSuccess?: (response: ServiceToken) => void;
}): JSX.Element {
  const handleClose = () => setOpen(false);
  const { user } = useAuth();
  const filteredTokenTypeOptions = tokenTypeOptions.filter(item => {
    if (modelSmithServiceTokenTypes.includes(item.value as ServiceTokenType)) {
      return user.featureFlags.FF_MODEL_SMITH;
    }
    if (aiServiceTokenTypes.includes(item.value as ServiceTokenType)) {
      return user.featureFlags.FF_AI_COPILOT;
    }
    return true;
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateIntegrationTokenPayload>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      tokenType: "",
      name: "",
      credentialData: {
        token: "",
      },
    },
  });
  const tokenTypeValue = watch("tokenType");

  useEffect(() => {
    if (givenTokenType) {
      setValue("tokenType", givenTokenType);
    }
  }, [givenTokenType]);

  const {
    mutateAsync: createIntegrationToken,
    data: createIntegrationTokenResponse,
    isPending: isCreateIntegrationTokenLoading,
    isSuccess: isCreateIntegrationTokenSuccess,
  } = useCreateIntegrationToken();

  const { data: embeddingTokensResponse, isLoading: isEmbeddingTokensLoading } =
    useIntegrationTokens(
      0,
      100,
      [],
      [
        {
          id: "tokenTypeIn",
          value: [ServiceTokenType.OpenAI, ServiceTokenType.Gemini],
        },
      ],
      false
    );

  const embeddingTokenOptions =
    embeddingTokensResponse?.data?.map(token => ({
      value: token.id,
      label: token.name,
      icon: getTokenTypeIcon(token.tokenType),
    })) ?? [];

  const handleCreateIntegrationToken = async (
    payload: CreateIntegrationTokenPayload
  ) => {
    const credentialData = payload.credentialData as {
      embeddingServiceTokenId?: number | null;
    };

    if (
      payload.tokenType !== ServiceTokenType.Anthropic ||
      credentialData.embeddingServiceTokenId == null
    ) {
      delete credentialData.embeddingServiceTokenId;
    }

    await createIntegrationToken(payload);
  };

  useEffect(() => {
    if (isCreateIntegrationTokenSuccess) {
      onSuccess && onSuccess(createIntegrationTokenResponse);
      handleClose();
    }
  }, [isCreateIntegrationTokenSuccess, createIntegrationTokenResponse]);

  const onSubmit = handleSubmit(handleCreateIntegrationToken);

  const SelectedCreateFormComponent =
    CreateFormComponentMap[tokenTypeValue] || CreateDefaultToken;

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New Integration Token
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to create your
              integration token.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Token Type <span className="text-red-400">*</span>
                </label>
                <SelectComponent
                  control={control}
                  name="tokenType"
                  rules={requiredValidation("Token Type")}
                  options={filteredTokenTypeOptions}
                  formatOptionLabel={option => (
                    <div className="flex items-center gap-2">
                      <img
                        className="w-5 py-2"
                        alt={option.label}
                        src={option.icon}
                      />
                      <span>{option.label}</span>
                    </div>
                  )}
                  placeholder="Select Token Type"
                />
                {errors.tokenType && (
                  <span className="text-xs text-rose-500">
                    {errors.tokenType.message}
                  </span>
                )}
              </div>

              <SelectedCreateFormComponent control={control} errors={errors} />

              {tokenTypeValue === ServiceTokenType.Anthropic && (
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                    Embeddings Provider Token
                  </label>
                  <SelectComponent
                    control={control}
                    name="credentialData.embeddingServiceTokenId"
                    options={embeddingTokenOptions}
                    isLoading={isEmbeddingTokensLoading}
                    formatOptionLabel={option => (
                      <div className="flex items-center gap-2">
                        <img
                          className="w-5 py-2"
                          alt={option.label}
                          src={option.icon}
                        />
                        <span>{option.label}</span>
                      </div>
                    )}
                    placeholder="Select OpenAI or Gemini token"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="create-integration-token-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="create-integration-token-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateIntegrationTokenLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
